import Link from 'next/link'
import { useRouter } from 'next/router'
import { useMemo } from 'react'
import clsx from 'clsx'

import { Badge } from '@wartek-id/badge'
import { Text } from '@wartek-id/text-v1'
import { Select } from '@wartek-id/select'
import { Button } from '@wartek-id/button-v1'
import { Chip } from '@wartek-id/chip'

import KinerjaSidebar from 'app/KinerjaShared/components/KinerjaSidebar'
import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderItem,
  TableRow,
  TableRowItem,
  TableShimmer,
} from 'app/KinerjaShared/components/Table'
import { useApiGetRiwayatTahunanByYear } from 'api/kinerja/kepala-sekolah/riwayat'
import { ROLES_LABEL } from 'app/KinerjaShared/constants/role'
import TableRowStates from 'app/KinerjaShared/components/TableRowStates'
import { PREDICATE_RECOMMENDATION_RESULT_TEXT } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/predicateRecommendation'
import {
  PENGELOLAAN_KINERJA_RIWAYAT_GURU_PATH,
  PENGELOLAAN_KINERJA_RIWAYAT_KS_V2_PATH,
} from 'app/KinerjaShared/constants/path'

import type { KinerjaRoleType } from 'types/kinerja'

type RiwayatV2Props = {
  year: number
  role?: RoleFilterType
}

export type RoleFilterType = 'ALL' | KinerjaRoleType

export function RiwayatV2({ year, role }: RiwayatV2Props) {
  const router = useRouter()

  const handleChangeFilter = ({
    year,
    role,
  }: {
    year?: string
    role?: RoleFilterType
  }) => {
    const currentParams = new URLSearchParams(window.location.search)

    if (year) currentParams.set('year', year)
    if (role) currentParams.set('role', role)

    router.push(
      {
        pathname: router.pathname,
        query: currentParams.toString(),
      },
      undefined
    )
  }

  // Get the riwayat list data
  const { isLoading, data: riwayatData } = useApiGetRiwayatTahunanByYear(
    {
      year: String(year),
    },
    { staleTime: 60 * 60 * 1000 }
  )

  // Filter by role
  const filteredData = useMemo(() => {
    if (role === 'ALL') return riwayatData?.data ?? []

    return riwayatData?.data?.filter((item) => item.role === role) || []
  }, [riwayatData, role])

  return (
    <div className="pl-64">
      <KinerjaSidebar sticky activeMenu="riwayat" />

      <main className="m-8 p-6 bg-white rounded-lg">
        <Text variant="display-md" as="h1">
          Riwayat Pengelolaan Kinerja
        </Text>
        <fieldset className="w-80 flex flex-col gap-1 mt-6">
          <Text variant="body" as="label" htmlFor="select-year">
            Pilih tahun
          </Text>
          <Select
            id="select-year"
            disabled={isLoading}
            value={year}
            onChange={(e) => handleChangeFilter({ year: e.target.value })}
          >
            {getYears().map((year, index) => (
              <option key={`option-${index}`} value={year}>
                {year}
              </option>
            ))}
          </Select>
        </fieldset>
        <section className="mt-10">
          <Text variant="heading-lg" as="h2">
            Rincian Tahunan
          </Text>

          <div className="mt-4 space-y-2">
            <Text variant="action" color="subdued">
              Jabatan
            </Text>

            <RoleFilter
              selectedRole={role}
              onSelect={(newActiveRole) =>
                handleChangeFilter({ role: newActiveRole })
              }
            />
          </div>

          <div className="mt-6">
            <Table withOuterBorder rounded>
              <TableHeader>
                <TableHeaderItem className="1/12">Periode</TableHeaderItem>
                <TableHeaderItem className="6/12">
                  Satuan Pendidikan
                </TableHeaderItem>
                <TableHeaderItem className="2/12">Jabatan</TableHeaderItem>
                <TableHeaderItem className="3/12">
                  Predikat Kinerja
                </TableHeaderItem>
                <TableHeaderItem />
              </TableHeader>

              <TableBody>
                {/* Loading state */}
                {isLoading && <TableShimmer columns={5} />}

                {/* Empty state */}
                {!isLoading && filteredData.length === 0 && (
                  <TableRowStates colSpan={4} type="EMPTY">
                    <div className="max-w-96">
                      Pastikan terdapat Pengelolaan Kinerja Anda yang sudah
                      selesai di tahun yang dipilih dan filter jabatan sudah
                      sesuai.
                    </div>
                  </TableRowStates>
                )}

                {/* Success state */}
                {filteredData.map((item) => {
                  const isGuru = item.role === 'GURU'

                  const detailUrl = {
                    pathname: isGuru
                      ? PENGELOLAAN_KINERJA_RIWAYAT_GURU_PATH
                      : PENGELOLAAN_KINERJA_RIWAYAT_KS_V2_PATH,
                    query: isGuru ? { year } : { riwayatId: item.id },
                  }

                  return (
                    <TableRow key={item.id}>
                      <TableRowItem>{year}</TableRowItem>
                      <TableRowItem>{item.satuanPendidikan}</TableRowItem>
                      <TableRowItem>{ROLES_LABEL[item.role]}</TableRowItem>
                      <TableRowItem>
                        <Badge
                          variant={
                            item.predicate === 'GOOD' ||
                            item.predicate === 'VERY_GOOD'
                              ? 'informational'
                              : 'critical'
                          }
                        >
                          {PREDICATE_RECOMMENDATION_RESULT_TEXT[item.predicate]}
                        </Badge>
                      </TableRowItem>
                      <TableRowItem>
                        <Link passHref href={detailUrl}>
                          <Button
                            as="a"
                            type={undefined}
                            size="sm"
                            color="white"
                            className="text-nowrap !inline-block"
                          >
                            Cek Rincian Riwayat
                          </Button>
                        </Link>
                      </TableRowItem>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        </section>
      </main>
    </div>
  )
}

export const ROLES_FILTER: Array<{ label: string; value: RoleFilterType }> = [
  {
    label: 'Semua',
    value: 'ALL',
  },
  {
    label: ROLES_LABEL['KS_DEFINITIF'],
    value: 'KS_DEFINITIF',
  },
  {
    label: ROLES_LABEL['KS_PLT'],
    value: 'KS_PLT',
  },
  {
    label: ROLES_LABEL['GURU'],
    value: 'GURU',
  },
]

type RoleFilterProps = {
  selectedRole: RoleFilterType
  onSelect: (role: RoleFilterType) => void
}
function RoleFilter({ selectedRole, onSelect }: RoleFilterProps) {
  return (
    <div className="flex space-x-2">
      {ROLES_FILTER.map((role) => {
        const isSelected = role.value === selectedRole

        return (
          <Chip
            size="sm"
            key={role.value}
            isActive={isSelected}
            role="option"
            aria-selected={isSelected}
            className={clsx(
              isSelected && '!text-blue-70 !outline-blue-70 !bg-blue-5'
            )}
            onClick={() => onSelect(role.value)}
          >
            {role.label}
          </Chip>
        )
      })}
    </div>
  )
}

/**
 * Returns a list of years starting from 2024 up to the current year.
 *
 * @returns {number[]} An array of years from 2024 to the current year.
 */
const getYears = (): number[] => {
  const startingYear = 2024
  const currentYear = new Date().getFullYear()
  const years: number[] = []

  for (let year = startingYear; year <= currentYear; year++) {
    years.push(year)
  }

  return years
}
